// queue/videoQueue.ts — the play queue (1.27.60, concept ⋯ "Add to queue ›"). Videos you line
// up to watch next: "Play next" puts one at the front, "Add to end of queue" at the back. Up
// next shows the queue above its tabs and Autoplay plays it first; arriving at a queued video
// takes it off the queue.
//
// Kept in this browser tab only (sessionStorage, try/catch — a private window or blocked
// storage just means the queue lasts until the page reloads). Nothing is sent to the server.
// Each entry keeps the card it was queued with, so the list draws without refetching.
import { create } from 'zustand';
import type { Post } from '@/core/models/post';
import type { VideoCreator, VideoItem } from '@/core/models/video';
import { toCreator, toVideoItem } from '../real/realItems';

const KEY = 'truxsocial:videoQueue';
/** Enough for an evening; a longer list is a playlist, not a queue. */
export const MAX_QUEUE = 50;

export interface QueuedVideo {
  id: string;
  item: VideoItem;
  creator: VideoCreator;
}

interface VideoQueueState {
  items: QueuedVideo[];
  playNext: (post: Post) => void;
  addToEnd: (post: Post) => void;
  remove: (id: string) => void;
  clear: () => void;
}

function load(): QueuedVideo[] {
  try {
    const raw = sessionStorage.getItem(KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((q): q is QueuedVideo => Boolean(q && q.id && q.item && q.creator))
      : [];
  } catch {
    return [];
  }
}

function save(items: QueuedVideo[]): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(items));
  } catch {
    // Storage blocked — the queue still works until the page reloads.
  }
}

function entry(post: Post): QueuedVideo | null {
  const item = toVideoItem(post);
  return item ? { id: post.id, item, creator: toCreator(post) } : null;
}

export const useVideoQueue = create<VideoQueueState>((set, get) => {
  const commit = (items: QueuedVideo[]) => {
    const capped = items.slice(0, MAX_QUEUE);
    save(capped);
    set({ items: capped });
  };
  return {
    items: load(),
    playNext: (post) => {
      const e = entry(post);
      if (e) commit([e, ...get().items.filter((q) => q.id !== e.id)]);
    },
    addToEnd: (post) => {
      const e = entry(post);
      if (e) commit([...get().items.filter((q) => q.id !== e.id), e]);
    },
    remove: (id) => {
      if (get().items.some((q) => q.id === id)) commit(get().items.filter((q) => q.id !== id));
    },
    clear: () => commit([]),
  };
});
